import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { PagesPaginateModel } from "../../../models/Paginate/PagesPaginate";
import { RefundsService } from "../../../services/Financial/Refunds/RefundsServices";
import { RefundsModel } from "../../../models/Financial/Refunds/RefundsModel";

interface ContextProps {
  params: any;
  setParams: Dispatch<SetStateAction<any>>;
  refunds: RefundsModel[];
  pages: PagesPaginateModel;
  setPages: Dispatch<SetStateAction<PagesPaginateModel>>;
  isLoading: boolean;
  error: string;
  setError: Dispatch<SetStateAction<string>>;
  handleList: () => void;
}

export const RefundsContext = createContext<ContextProps>({} as ContextProps);

interface RefundsProviderProps {
  children: ReactNode;
}

export const RefundsProvider = ({ children }: RefundsProviderProps) => {
  const [params, setParams] = useState<any>(null);
  const [pages, setPages] = useState<PagesPaginateModel>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [refunds, setRefunds] = useState<RefundsModel[]>([]);

  const refundsService = useRef(new RefundsService());
  const location = useLocation();

  const handleList = useCallback(async () => {
    if (!params || !location.pathname.includes("/financial/refunds")) {
      return false;
    }

    try {
      setIsLoading(true);
      const [_Response, _Error] = await refundsService.current.list(params);
      setIsLoading(false);

      if (!!_Error) {
        return setError(_Response?.message || _Error);
      }

      setRefunds(_Response?.data?.items);
      setPages(_Response?.data?.pagination);
      return setError("");
    } catch (e) {
      setIsLoading(false);
      setError("Não foi possível carregar os reembolsos do sistema.");
    }
  }, [params, location.pathname]);

  useEffect(() => {
    !!error && toast.error(error);
  }, [error]);

  useEffect(() => {
    handleList().then();
  }, [handleList]);

  return (
    <RefundsContext.Provider
      value={{
        params,
        setParams,
        refunds,
        pages,
        setPages,
        isLoading,
        error,
        setError,
        handleList,
      }}
    >
      {children}
    </RefundsContext.Provider>
  );
};

export const useRefunds = () => useContext(RefundsContext);
